/**
 * Google Analytics 4, loaded only after the visitor has agreed to it.
 *
 * **Nothing here runs until consent is granted — not the script, not a request, not a cookie.** The
 * alternative Google documents is Consent Mode: load `gtag.js` immediately with consent defaulted
 * to denied, then update it. That still fetches a script from Google and still tells them somebody
 * arrived, on a page whose own policy says analytics happen with permission. This asks first.
 *
 * **The measurement ID is a build-time value and there is no default.** Hardcoding the live site's
 * `G-27G61LCEH7` would send every developer's clicks and every preview deployment into the
 * production property, where the traffic cannot be told apart afterwards. Unset means no analytics,
 * which is the right answer everywhere except the one host that sets it.
 *
 * **Page views are sent by hand, not by `gtag.js`.** This is a client-routed application: the
 * script's own automatic `page_view` fires once, at load, and every navigation after it — `/kk` to
 * `/kk/book` to step two to step three — would be invisible. `send_page_view: false` turns the
 * automatic one off so the router's first event is not also a duplicate of it.
 */

const CONTAINER_SOURCE = 'https://www.googletagmanager.com/gtag/js';

declare global {
    interface Window {
        dataLayer?: unknown[];
        gtag?: (...args: unknown[]) => void;
        /** Google's documented per-property opt-out: `window['ga-disable-G-XXXX'] = true`. */
        [flag: `ga-disable-${string}`]: boolean | undefined;
    }
}

let started: string | null = null;

/**
 * The property to report to, or null when this build has none.
 *
 * Validated rather than trusted: a `VITE_GA_MEASUREMENT_ID` that arrived empty, quoted, or as the
 * literal string `undefined` from a shell would otherwise be loaded as a container name and fail
 * inside Google's script, where nothing on this side can see it.
 */
export function measurementId(): string | null {
    const configured: unknown = import.meta.env.VITE_GA_MEASUREMENT_ID;

    return typeof configured === 'string' && /^G-[A-Z0-9]{4,}$/.test(configured) ? configured : null;
}

/** Whether analytics is running, which is the only thing the rest of the app needs to ask. */
export function analyticsStarted(): boolean {
    return started !== null;
}

/**
 * Loads `gtag.js` and configures the property. Safe to call again: the second call does nothing,
 * because the effect that calls this re-runs on every consent change.
 */
export function startAnalytics(id: string): void {
    if (started !== null || typeof document === 'undefined') {
        return;
    }

    started = id;

    // Re-enable the property if a refusal earlier in this same page switched it off.
    window[`ga-disable-${id}`] = false;

    window.dataLayer = window.dataLayer ?? [];
    window.gtag = window.gtag ?? gtag;

    window.gtag('js', new Date());
    window.gtag('config', id, { send_page_view: false });

    const script = document.createElement('script');
    script.async = true;
    script.src = `${CONTAINER_SOURCE}?id=${encodeURIComponent(id)}`;
    document.head.appendChild(script);
}

/**
 * Stops reporting and removes what was already stored.
 *
 * The script cannot be unloaded once it is in the document, so this uses the opt-out flag Google
 * documents for exactly this — `window['ga-disable-<ID>']`, which every subsequent call checks —
 * and then deletes the cookies it set. A visitor who withdraws consent is entitled to both halves:
 * no further reporting, and no identifier left behind.
 */
export function stopAnalytics(): void {
    if (typeof document === 'undefined') {
        return;
    }

    const id = started ?? measurementId();

    if (id !== null) {
        window[`ga-disable-${id}`] = true;
    }

    started = null;
    clearAnalyticsCookies();
}

/**
 * Reports one page view.
 *
 * `page_location` is the absolute URL, because GA derives the host and the query string from it —
 * a `page_path` alone leaves a report that cannot tell one language's page from the other's.
 */
export function trackPageView(url: string, title: string): void {
    window.gtag?.('event', 'page_view', { page_location: url, page_title: title });
}

/**
 * Reports a share, as GA4's recommended `share` event, so the shares sent can be set against the
 * visits their tagged links bring back. Does nothing without consent: `gtag` is only defined once
 * analytics has started.
 */
export function trackShare(method: string, contentType: string, itemId: string): void {
    window.gtag?.('event', 'share', { method, content_type: contentType, item_id: itemId });
}

/**
 * The real `gtag`, which pushes its own `arguments` object rather than an array.
 *
 * That is not a stylistic copy of Google's snippet. `gtag.js` tells a gtag command apart from a
 * GTM-style data-layer object by the shape of what was pushed, and an array is the wrong shape.
 */
function gtag(): void {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
}

/**
 * `_ga`, the per-property `_ga_<container>`, and `_gid`.
 *
 * Expired at every domain they could have been written on: `gtag.js` sets them on the registrable
 * domain, so expiring one for `www.example.kz` does not remove a cookie written for `.example.kz` —
 * and a deletion that silently misses is worse than not offering the button.
 */
function clearAnalyticsCookies(): void {
    const names = document.cookie
        .split(';')
        .map((entry) => entry.split('=')[0]?.trim() ?? '')
        .filter((name) => name === '_ga' || name.startsWith('_ga_') || name === '_gid');

    const labels = window.location.hostname.split('.');
    const domains = ['', ...labels.map((_, index) => `.${labels.slice(index).join('.')}`)];

    for (const name of new Set(names)) {
        for (const domain of domains) {
            const scope = domain === '' ? '' : `; domain=${domain}`;
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/${scope}`;
        }
    }
}
